import React, { useState } from 'react';

const Calendar = () => {
  const [selectedDate] = useState(new Date());
  const [view, setView] = useState('week');
  const [, setSelectedEvent] = useState(null);

  const events = [
    // Sample data - will be replaced with Google Calendar data
    {
      id: 1,
      title: 'Session with John Doe',
      start: '2023-01-15T09:00:00',
      end: '2023-01-15T10:00:00',
      client: 'John Doe',
      therapist: 'Dr. Sarah Johnson',
      type: 'session',
      status: 'confirmed'
    }
  ];

  const renderCalendarHeader = () => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return (
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">
          {selectedDate.toLocaleDateString(undefined, options)}
        </h1>
        <div className="flex space-x-4">
          <button
            onClick={() => setView('day')}
            className={`px-4 py-2 rounded ${
              view === 'day'
                ? 'bg-brand-600 text-white'
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            Day
          </button>
          <button
            onClick={() => setView('week')}
            className={`px-4 py-2 rounded ${
              view === 'week'
                ? 'bg-brand-600 text-white'
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            Week
          </button>
          <button
            onClick={() => setView('month')}
            className={`px-4 py-2 rounded ${
              view === 'month'
                ? 'bg-brand-600 text-white'
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            Month
          </button>
        </div>
      </div>
    );
  };

  const renderCalendarView = () => {
    if (view === 'day') {
      return (
        <div className="bg-white rounded-lg shadow">
          <div className="grid grid-cols-1">
            {Array.from({ length: 24 }, (_, i) => (
              <div key={i} className="border-b p-2">
                <div className="text-sm text-gray-500">
                  {i.toString().padStart(2, '0')}:00
                </div>
                {events
                  .filter(event => {
                    const eventStart = new Date(event.start);
                    return (
                      eventStart.getDate() === selectedDate.getDate() &&
                      eventStart.getMonth() === selectedDate.getMonth() &&
                      eventStart.getFullYear() === selectedDate.getFullYear() &&
                      eventStart.getHours() === i
                    );
                  })
                  .map(event => (
                    <div
                      key={event.id}
                      className="mt-2 p-2 bg-brand-100 rounded cursor-pointer"
                      onClick={() => setSelectedEvent(event)}
                    >
                      <div className="font-medium">{event.title}</div>
                      <div className="text-sm text-gray-500">
                        {new Date(event.start).toLocaleTimeString()} -{' '}
                        {new Date(event.end).toLocaleTimeString()}
                      </div>
                    </div>
                  ))}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (view === 'week') {
      return (
        <div className="bg-white rounded-lg shadow">
          <div className="grid grid-cols-7">
            {Array.from({ length: 7 }, (_, i) => {
              const date = new Date(selectedDate);
              date.setDate(selectedDate.getDate() - selectedDate.getDay() + i);
              return (
                <div key={i} className="border-b p-2">
                  <div className="text-sm font-medium">
                    {date.toLocaleDateString(undefined, { weekday: 'short' })}
                  </div>
                  <div className="text-sm text-gray-500">
                    {date.getDate()}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-7">
            {Array.from({ length: 7 }, (_, i) => {
              const date = new Date(selectedDate);
              date.setDate(selectedDate.getDate() - selectedDate.getDay() + i);
              return (
                <div key={i} className="border-r p-2 min-h-[500px]">
                  {events
                    .filter(event => {
                      const eventDate = new Date(event.start);
                      return (
                        eventDate.getDate() === date.getDate() &&
                        eventDate.getMonth() === date.getMonth() &&
                        eventDate.getFullYear() === date.getFullYear()
                      );
                    })
                    .map(event => (
                      <div
                        key={event.id}
                        className="mt-2 p-2 bg-brand-100 rounded cursor-pointer"
                        onClick={() => setSelectedEvent(event)}
                      >
                        <div className="font-medium">{event.title}</div>
                        <div className="text-sm text-gray-500">
                          {new Date(event.start).toLocaleTimeString()} -{' '}
                          {new Date(event.end).toLocaleTimeString()}
                        </div>
                      </div>
                    ))}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (view === 'month') {
      const firstDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
      const lastDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
      const daysInMonth = lastDay.getDate();
      const startingDay = firstDay.getDay();

      return (
        <div className="bg-white rounded-lg shadow">
          <div className="grid grid-cols-7">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="border-b p-2 text-center font-medium">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {Array.from({ length: 42 }, (_, i) => {
              const day = i - startingDay + 1;
              return (
                <div
                  key={i}
                  className={`border p-2 min-h-[100px] ${
                    day < 1 || day > daysInMonth ? 'bg-gray-50' : ''
                  }`}
                >
                  {day > 0 && day <= daysInMonth && (
                    <>
                      <div className="text-sm">{day}</div>
                      {events
                        .filter(event => {
                          const eventDate = new Date(event.start);
                          return (
                            eventDate.getDate() === day &&
                            eventDate.getMonth() === selectedDate.getMonth() &&
                            eventDate.getFullYear() === selectedDate.getFullYear()
                          );
                        })
                        .map(event => (
                          <div
                            key={event.id}
                            className="mt-1 p-1 bg-brand-100 rounded text-xs cursor-pointer"
                            onClick={() => setSelectedEvent(event)}
                          >
                            {event.title}
                          </div>
                        ))}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }
  };

  return (
    <div className="flex">
      <div className="flex-1 p-8">
        {renderCalendarHeader()}
        {renderCalendarView()}
      </div>
    </div>
  );
};

export default Calendar;
